import React, { useEffect, useState } from 'react';

import { Box, Flex, Stack, xcss } from '@atlaskit/primitives';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

import { RequestAdditionalFeedback } from 'src/components/code-reviewer-feedback/src/components/request-additional-feedback';
import { ThumbsUpThumbsDown } from 'src/components/code-reviewer-feedback/src/components/thumbs-up-thumbs-down';
import { CodeReviewerTextSubmissionState } from 'src/components/code-reviewer-feedback/src/types';
import { commentHasCodeSuggestions } from 'src/components/code-reviewer-feedback/src/util';
import { SendAnalyticsEvent } from 'src/components/conversation/src/components/types';
import {
  actionSubjectIds,
  CodeReviewerFeedbackSentiment,
  EventAttributes,
  EventData,
} from 'src/components/conversation/src/internal/analytics';
import {
  DEFAULT_CODE_REVIEWER_COMMENT_UUIDS,
  identifyCodeReviewerComment,
} from 'src/components/conversation/src/utils/code-reviewer-comment-identifiers';
import { FabricComment } from 'src/components/conversation-provider/types';
import { PullRequest } from 'src/components/types';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { useConfigString } from 'src/contexts/statsig-config-provider';
import store from 'src/utils/store';

type StoreValue = {
  collapsed: boolean;
  sentiment: CodeReviewerFeedbackSentiment;
};

const CODE_REVIEWER_COMMENT_STORE_KEY = 'code-reviewer-comment-id';

export type Props = {
  comment: FabricComment;
  isFileLevelComment: boolean;
  sendAnalyticsEvent: SendAnalyticsEvent;
  pullRequest?: PullRequest;
  collapseComment?: () => void;
};

export const CodeReviewerFeedback = ({
  comment,
  isFileLevelComment,
  sendAnalyticsEvent,
  pullRequest,
  collapseComment,
}: Props) => {
  const [sentiment, setSentiment] =
    useState<CodeReviewerFeedbackSentiment>('neutral');
  const [textSubmissionState, setTextSubmissionState] =
    useState<CodeReviewerTextSubmissionState>('hidden');

  const isCodeReviewerFeedbackEnabled = useStatsigGate(
    StatsigFeatureKeys.enableCodeReviewerFeedbackUi
  ) as boolean;

  const commentUUIDs = useConfigString(
    StatsigFeatureKeys.codeReviewerCommentUuidConfig,
    DEFAULT_CODE_REVIEWER_COMMENT_UUIDS
  );

  const isCodeReviewerComment =
    identifyCodeReviewerComment(comment.createdBy, commentUUIDs) &&
    isFileLevelComment;

  const storeId = `${CODE_REVIEWER_COMMENT_STORE_KEY}::${comment.commentId}`;

  useEffect(() => {
    const maybeItem: StoreValue | null = store.get(storeId);
    if (maybeItem && collapseComment) {
      setSentiment(maybeItem.sentiment);
      if (maybeItem.collapsed) {
        collapseComment();
      }
    }
  }, [comment.commentId, collapseComment, storeId]);

  if (!isCodeReviewerFeedbackEnabled || !isCodeReviewerComment) {
    return <></>;
  }

  const commonEventData = (
    additionalAttributes?: EventAttributes
  ): EventData => ({
    objectId: comment.commentId.toString(),
    actionSubject: 'code-reviewer',
    attributes: {
      prAuthorId: pullRequest?.author?.account_id,
      prURL: pullRequest?.links.html.href,
      commentId: comment.commentId,
      hasCodeSuggestions: commentHasCodeSuggestions(comment),
      ...additionalAttributes,
    },
  });

  const handleFeedbackClick = (givenSentiment: 'positive' | 'negative') => {
    switch (givenSentiment) {
      case 'positive':
        sendAnalyticsEvent({
          actionSubjectId: actionSubjectIds.codeReviewerThumbsUp,
          ...commonEventData({ codeReviewerFeedbackSentiment: givenSentiment }),
        });
        store.setStrictlyNecessaryItem(storeId, {
          collapsed: false,
          sentiment: 'positive',
        });
        break;
      case 'negative':
        sendAnalyticsEvent({
          actionSubjectId: actionSubjectIds.codeReviewerThumbsDown,
          ...commonEventData({ codeReviewerFeedbackSentiment: givenSentiment }),
        });
        store.setStrictlyNecessaryItem(storeId, {
          collapsed: true,
          sentiment: 'negative',
        });
        break;
    }

    setTextSubmissionState('pending');
    setSentiment(givenSentiment);
  };

  const handleTextFeedbackSent = () => {
    sendAnalyticsEvent({
      actionSubjectId: actionSubjectIds.codeReviewerTextFeedbackSubmission,
      ...commonEventData(),
    });
    setTextSubmissionState('submitted');
    if (sentiment === 'negative' || sentiment === 'neutral') {
      // this has to be nested this way for the typechecker
      if (collapseComment) {
        collapseComment();
      }
    }
  };

  const boxStyle = xcss({
    borderRadius: 'border.radius.100',
  });

  return (
    <Box padding="space.100" xcss={boxStyle}>
      <Flex gap="space.100">
        <Flex
          alignItems="center"
          justifyContent="center"
          xcss={xcss({ height: '24px' })}
        >
          🤔
        </Flex>
        <Stack xcss={xcss({ flex: 1 })}>
          <ThumbsUpThumbsDown
            sentiment={sentiment}
            handleFeedbackClick={handleFeedbackClick}
          />
          <RequestAdditionalFeedback
            prUrl={pullRequest?.links.html.href}
            sentiment={sentiment}
            textSubmissionState={textSubmissionState}
            handleTextFeedbackSent={handleTextFeedbackSent}
            hasCodeSuggestions={commentHasCodeSuggestions(comment)}
          />
        </Stack>
      </Flex>
    </Box>
  );
};
